import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../services/Helper';
import './MallDetail.css';

const MallDetail = () => {
  const { mallId } = useParams();
  const navigate = useNavigate();
  const [mall, setMall] = useState(null);
  const [shops, setShops] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activeTab, setActiveTab] = useState('shops');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [mallsRes, shopsRes, offersRes] = await Promise.all([
          fetch(`${BACKEND_URL}/nearbe/getallmalls`),
          fetch(`${BACKEND_URL}/nearbe/getmallshops/${mallId}`),
          fetch(`${BACKEND_URL}/nearbe/getmalloffers/${mallId}`),
        ]);
        const mallsData = await mallsRes.json();
        const shopsData = await shopsRes.json();
        const offersData = await offersRes.json();

        const found = (mallsData.malls || []).find(m => m._id === mallId);
        setMall(found || null);
        setShops(shopsData.shops || []);
        setOffers(offersData.offers || []);
      } catch {
        /* keep empty */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [mallId]);

  const categories = ['All', ...new Set(shops.map(s => s.category).filter(Boolean))];
  const filteredShops = categoryFilter === 'All' ? shops : shops.filter(s => s.category === categoryFilter);
  const boostedOffers = offers.filter(o => o.isBoosted);
  const normalOffers = offers.filter(o => !o.isBoosted);
  const shopsWithReels = shops.filter(s => s.reelLink);
  const shopsWithPhotos = shops.filter(s => s.shopImage);

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  const goToShop = (shop) =>
    navigate('/userdashboard/restaurants/info', { state: { hotels: shop } });

  if (loading) {
    return (
      <div className="malldetail-loading">
        <div className="malldetail-spinner" />
        <p>Loading mall...</p>
      </div>
    );
  }

  if (!mall) {
    return (
      <div className="malldetail-notfound">
        <p>Mall not found.</p>
        <button onClick={() => navigate('/userdashboard/malls')}>Back to Malls</button>
      </div>
    );
  }

  return (
    <div className="malldetail-root">
      {/* Hero */}
      <div
        className="malldetail-hero"
        style={mall.mallImage ? { backgroundImage: `url(${mall.mallImage})` } : {}}
      >
        <div className="malldetail-hero-overlay">
          <button className="malldetail-back-btn" onClick={() => navigate('/userdashboard/malls')}>
            ← Malls
          </button>
          <div className="malldetail-hero-info">
            <h1 className="malldetail-name">{mall.mallName}</h1>
            <p className="malldetail-city">📍 {mall.mallAddress}, {mall.mallCity}</p>
            {mall.description && <p className="malldetail-desc">{mall.description}</p>}
            <div className="malldetail-hero-chips">
              <span className="malldetail-chip">🏪 {mall.shopCount ?? shops.length} Shops</span>
              <span className="malldetail-chip">🏷️ {mall.offerCount ?? offers.length} Offers</span>
              {shopsWithReels.length > 0 && (
                <span className="malldetail-chip">🎬 {shopsWithReels.length} Reels</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="malldetail-tabs">
        {[
          { key: 'shops',  label: `🏪 Shops (${shops.length})` },
          { key: 'offers', label: `🏷️ Offers (${offers.length})` },
          { key: 'photos', label: `📸 Photos` },
          { key: 'reels',  label: `🎬 Reels` },
        ].map(t => (
          <button
            key={t.key}
            className={`malldetail-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── SHOPS TAB ── */}
      {activeTab === 'shops' && (
        <div className="malldetail-content">
          {categories.length > 1 && (
            <div className="malldetail-cats">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`malldetail-cat-chip ${categoryFilter === cat ? 'active' : ''}`}
                  onClick={() => setCategoryFilter(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
          {filteredShops.length === 0 ? (
            <div className="malldetail-empty">No shops in this category yet.</div>
          ) : (
            <div className="malldetail-shops-grid">
              {filteredShops.map(shop => (
                <div key={shop._id} className="malldetail-shop-card" onClick={() => goToShop(shop)}>
                  <div className="malldetail-shop-img-wrap">
                    {shop.shopImage
                      ? <img src={shop.shopImage} alt={shop.shopName} className="malldetail-shop-img" />
                      : <div className="malldetail-shop-img-placeholder">🏪</div>
                    }
                    {(shop.floor || shop.shopUnit) && (
                      <span className="malldetail-floor-badge">
                        {[shop.floor, shop.shopUnit].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>
                  <div className="malldetail-shop-body">
                    <h3 className="malldetail-shop-name">{shop.shopName}</h3>
                    <p className="malldetail-shop-cat">{shop.category}</p>
                    {shop.homeDelivery && (
                      <span className="malldetail-delivery-badge">🚚 Free Delivery</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── OFFERS TAB ── */}
      {activeTab === 'offers' && (
        <div className="malldetail-content">
          {offers.length === 0 ? (
            <div className="malldetail-empty">No active offers in this mall right now.</div>
          ) : (
            <>
              {boostedOffers.length > 0 && (
                <section className="malldetail-offer-section">
                  <h3 className="malldetail-offer-section-title">🔥 Promoted Offers</h3>
                  <div className="malldetail-offers-grid">
                    {boostedOffers.map(offer => (
                      <OfferCard key={offer._id} offer={offer} formatDate={formatDate} boosted />
                    ))}
                  </div>
                </section>
              )}
              {normalOffers.length > 0 && (
                <section className="malldetail-offer-section">
                  <h3 className="malldetail-offer-section-title">🏷️ Shop Deals</h3>
                  <div className="malldetail-offers-grid">
                    {normalOffers.map(offer => (
                      <OfferCard key={offer._id} offer={offer} formatDate={formatDate} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      )}

      {/* ── PHOTOS TAB ── */}
      {activeTab === 'photos' && (
        <div className="malldetail-content">
          {shopsWithPhotos.length === 0 ? (
            <div className="malldetail-empty">No shop photos available yet.</div>
          ) : (
            <div className="malldetail-photos-grid">
              {shopsWithPhotos.map(shop => (
                <div
                  key={shop._id}
                  className="malldetail-photo-tile"
                  onClick={() => goToShop(shop)}
                  title={shop.shopName}
                >
                  <img src={shop.shopImage} alt={shop.shopName} className="malldetail-photo-img" />
                  <div className="malldetail-photo-label">
                    <span>{shop.shopName}</span>
                    {shop.category && <span className="malldetail-photo-cat">{shop.category}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── REELS TAB ── */}
      {activeTab === 'reels' && (
        <div className="malldetail-content">
          {shopsWithReels.length === 0 ? (
            <div className="malldetail-empty">
              <span style={{ fontSize: 40 }}>🎬</span>
              <p>No shops have shared a reel yet.</p>
            </div>
          ) : (
            <div className="malldetail-reels-grid">
              {shopsWithReels.map(shop => (
                <div key={shop._id} className="malldetail-reel-card">
                  <div className="malldetail-reel-img-wrap">
                    {shop.shopImage
                      ? <img src={shop.shopImage} alt={shop.shopName} className="malldetail-reel-img" />
                      : <div className="malldetail-reel-img-placeholder" />
                    }
                    <div className="malldetail-reel-play-icon">▶</div>
                  </div>
                  <div className="malldetail-reel-body">
                    <h4 className="malldetail-reel-name">{shop.shopName}</h4>
                    <p className="malldetail-reel-cat">{shop.category}</p>
                    {(shop.floor || shop.shopUnit) && (
                      <p className="malldetail-reel-floor">
                        {[shop.floor, shop.shopUnit].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <a
                      href={shop.reelLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="malldetail-reel-btn"
                      onClick={e => e.stopPropagation()}
                    >
                      ▶ Watch Reel
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* NI floating button */}
      <button className="malldetail-ni-fab" onClick={() => navigate('/userdashboard/ni')}>
        ✨ NI
      </button>
    </div>
  );
};

const OfferCard = ({ offer, formatDate, boosted }) => (
  <div className={`malldetail-offer-card ${boosted ? 'boosted' : ''}`}>
    {offer.postedBy?.shopImage && (
      <div className="malldetail-offer-img-wrap">
        <img src={offer.postedBy.shopImage} alt={offer.postedBy.shopName} className="malldetail-offer-img" />
        <div className="malldetail-offer-img-overlay" />
      </div>
    )}
    <div className="malldetail-offer-body">
      <div className="malldetail-offer-badge">{boosted ? '🔥 Promoted' : '🏷️ Deal'}</div>
      <div className="malldetail-offer-pct">{offer.percentage}% OFF</div>
      <h4 className="malldetail-offer-title">{offer.title}</h4>
      {offer.description && <p className="malldetail-offer-desc">{offer.description}</p>}
      {offer.postedBy && (
        <div className="malldetail-offer-shop">
          <span className="malldetail-offer-shop-name">{offer.postedBy.shopName}</span>
          {offer.postedBy.category && (
            <span className="malldetail-offer-shop-cat">{offer.postedBy.category}</span>
          )}
        </div>
      )}
      <p className="malldetail-offer-valid">Valid till {formatDate(offer.validTill)}</p>
    </div>
  </div>
);

export default MallDetail;
